import { Editor } from "@tiptap/core";
import { DOMSerializer } from "@tiptap/pm/model";

import { createEditorExtensions } from "./editorExtensions.mjs";
import {
  createEditorContextSnapshot,
  readCurrentBlock,
  readSelection,
  readSelectionRect
} from "./editorContext.mjs";
import {
  executeEditorCommand,
  getCommandsForContext,
  getEditorCommandState
} from "./editorCommandRegistry.mjs";
import { executeSlashCommand } from "./slashCommands.mjs";

const BLOCK_DESCRIPTORS = new WeakMap();

function getEditorSelection(editor) {
  return readSelection(editor);
}

function blockFingerprint(node) {
  try {
    const fingerprint = JSON.stringify(node?.toJSON());
    return typeof fingerprint === "string" ? fingerprint : null;
  } catch {
    return null;
  }
}

function escapePlainText(value) {
  return String(value)
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]*>/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function createPasteTransform(transformPastedHTML) {
  if (typeof transformPastedHTML !== "function") return undefined;
  return (html) => {
    try {
      const transformed = transformPastedHTML(html);
      return typeof transformed === "string" ? transformed : escapePlainText(html);
    } catch {
      return escapePlainText(html);
    }
  };
}

function getCurrentBlockEntry(editor) {
  try {
    if (editor?.isDestroyed === true) return null;
    const state = editor?.state;
    const block = readCurrentBlock(state);
    const from = block?.from;
    const expectedTo = block?.to;
    const documentSize = state?.doc?.content?.size;
    if (!Number.isInteger(from)
      || from < 0
      || !Number.isInteger(expectedTo)
      || !Number.isInteger(documentSize)) return null;
    const node = state.doc.nodeAt(from);
    const nodeSize = node?.nodeSize;
    const to = from + nodeSize;
    if (node?.isBlock !== true
      || node?.type?.name !== block.type
      || !Number.isInteger(nodeSize)
      || nodeSize < 1
      || !Number.isSafeInteger(to)
      || to > documentSize
      || expectedTo !== to - 1) return null;
    return { state, node, from, to };
  } catch {
    return null;
  }
}

function getCurrentBlockClipboardData(editor) {
  try {
    const entry = getCurrentBlockEntry(editor);
    if (!entry) return null;
    const ownerDocument = editor?.view?.dom?.ownerDocument;
    if (!ownerDocument) return null;
    const serialized = DOMSerializer.fromSchema(editor.schema)
      .serializeNode(entry.node, { document: ownerDocument });
    const html = serialized?.outerHTML;
    const text = entry.node.textBetween(0, entry.node.content.size, "\n");
    const fingerprint = blockFingerprint(entry.node);
    if (typeof html !== "string" || !html || typeof text !== "string" || fingerprint === null) return null;
    const data = { text, html };
    BLOCK_DESCRIPTORS.set(data, {
      editor,
      doc: entry.state.doc,
      from: entry.from,
      nodeSize: entry.node.nodeSize,
      fingerprint
    });
    return data;
  } catch {
    return null;
  }
}

function getCapturedBlockEntry(editor, expectedBlock) {
  try {
    const descriptor = BLOCK_DESCRIPTORS.get(expectedBlock);
    if (!descriptor || descriptor.editor !== editor) return null;
    const state = editor?.state;
    if (state?.doc !== descriptor.doc) return null;
    const node = state.doc.nodeAt(descriptor.from);
    const to = descriptor.from + descriptor.nodeSize;
    if (node?.isBlock !== true
      || node.nodeSize !== descriptor.nodeSize
      || !Number.isSafeInteger(to)
      || to > state.doc.content.size
      || blockFingerprint(node) !== descriptor.fingerprint) return null;
    return { state, node, from: descriptor.from, to };
  } catch {
    return null;
  }
}

function deleteCurrentBlock(editor, expectedBlock) {
  try {
    if (editor?.isEditable !== true) return false;
    const entry = expectedBlock === undefined
      ? getCurrentBlockEntry(editor)
      : getCapturedBlockEntry(editor, expectedBlock);
    if (!entry) return false;
    const transaction = entry.state.tr.delete(entry.from, entry.to);
    if (transaction.docChanged !== true) return false;
    try {
      editor.view.dispatch(transaction);
      return true;
    } catch {
      try {
        return editor.state.doc.eq(transaction.doc) === true;
      } catch {
        return false;
      }
    }
  } catch {
    return false;
  }
}

export function createTiptapEditor({
  element,
  content,
  createEditor = (options) => new Editor(options),
  onChange = () => {},
  onSelectionChange = () => {},
  onContextChange = () => {},
  onKeyDown = () => false,
  onTitleFromPaste = () => {},
  transformPastedHTML
}) {
  const pasteTransform = createPasteTransform(transformPastedHTML);
  let contextEmissions = 0;
  function emitContext(editor, focused) {
    const snapshot = createEditorContextSnapshot(editor);
    if (typeof focused === "boolean") snapshot.focused = focused;
    if (!snapshot.focused) snapshot.slash = null;
    contextEmissions += 1;
    try {
      if (typeof onContextChange === "function") onContextChange(snapshot);
    } catch {}
    return snapshot;
  }
  const options = {
    element,
    content,
    extensions: createEditorExtensions({ onTitleFromPaste }),
    onUpdate: ({ editor }) => onChange(editor.getJSON()),
    onSelectionUpdate: ({ editor }) => onSelectionChange(getEditorSelection(editor)),
    onTransaction: ({ editor, transaction }) => {
      try {
        if (transaction?.getMeta?.("focus") || transaction?.getMeta?.("blur")) return;
      } catch {}
      emitContext(editor);
    },
    onFocus: ({ editor }) => emitContext(editor, true),
    onBlur: ({ editor }) => emitContext(editor, false),
    editorProps: {
      handleKeyDown: (_view, event) => {
        try {
          return typeof onKeyDown === "function" && onKeyDown(event) === true;
        } catch {
          return false;
        }
      }
    }
  };
  if (pasteTransform) options.editorProps.transformPastedHTML = pasteTransform;

  const editor = createEditor(options);

  function mutateAndPushContext(mutation) {
    const before = contextEmissions;
    const result = mutation();
    if (contextEmissions === before) emitContext(editor);
    return result;
  }

  return {
    destroy: () => editor.destroy(),
    focus: () => editor.commands.focus(),
    getDocument: () => editor.getJSON(),
    setDocument: (doc) => mutateAndPushContext(() => editor.commands.setContent(doc, { emitUpdate: false })),
    setEditable: (editable) => mutateAndPushContext(() => editor.setEditable(editable)),
    getText: () => editor.getText(),
    getSelection: () => getEditorSelection(editor),
    getSelectionRect: () => readSelectionRect(editor),
    getContextSnapshot: () => createEditorContextSnapshot(editor),
    getCurrentBlockClipboardData: () => getCurrentBlockClipboardData(editor),
    deleteCurrentBlock: (expectedBlock) => deleteCurrentBlock(editor, expectedBlock),
    replaceSelection(text, expected) {
      const { from, to } = expected ?? {};
      const size = editor.state.doc.content.size;
      if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to < from || to > size) return false;
      try {
        if (editor.state.doc.textBetween(from, to) !== expected.text) return false;
        return editor.commands.insertContentAt({ from, to }, text) !== false;
      } catch {
        return false;
      }
    },
    runCommand: (command, payload) => executeEditorCommand(editor, command, payload),
    runSlashCommand: (command, range) => executeSlashCommand(editor, command, range),
    getCommandState: (command, payload) => getEditorCommandState(editor, command, payload),
    getCommandsForContext,
    isActive: (command, payload) => getEditorCommandState(editor, command, payload).active,
    canRunCommand: (command, payload) => getEditorCommandState(editor, command, payload).enabled,
    insertImagePlaceholder(attrs = {}) {
      try {
        return editor.chain().focus().insertContent({ type: "imagePlaceholder", attrs }).run();
      } catch {
        return false;
      }
    }
  };
}
